import { useCallback, useMemo } from 'react';

import { usePaypalOaltInitiateOnDemand, usePaypalProcessOnDemand } from '@api';
import { useApplicationBuildContext } from '@context/ApplicationBuildContext';
import { EOrderAsLastTimeState } from '@model';
import { PAGES } from '@services/routingService';
import { EPage } from '@types';
import { formatUrl } from '@utils/text';

import { EPaypalComponentState } from './components/model';
import { IOnInit, IPaypalApi } from './model';

export const usePaypalApi = ({ setComponentState, setPageState, onSuccess }: IPaypalApi) => {
  const {
    settings: { websiteHostAddress },
  } = useApplicationBuildContext();

  const { request: initiate } = usePaypalOaltInitiateOnDemand();
  const { request: process } = usePaypalProcessOnDemand();

  const urlCartPage = formatUrl({ urlBase: websiteHostAddress, path: PAGES[EPage.CART].internal! });

  const onInit = useCallback(async (): Promise<IOnInit | null> => {
    const response = await initiate({
      cancelUrl: urlCartPage,
      returnUrl: urlCartPage,
    });

    if (!response?.paypalOrderId) {
      setComponentState(EPaypalComponentState.ERROR);
      setPageState(EOrderAsLastTimeState.ERROR);
      return null;
    }

    //when is available token for payment orderId will be used
    return { paypalOrderId: response.paypalOrderId, orderId: response.orderId };
  }, [initiate, setComponentState, setPageState, urlCartPage]);

  const onProcess = useCallback(
    async (orderId: string) => {
      const response = await process({ paypalOrderId: orderId });

      if (!response || !response.orderId) {
        setPageState(EOrderAsLastTimeState.ERROR);
        setComponentState(EPaypalComponentState.ERROR);
        return;
      }

      onSuccess();
    },
    [onSuccess, process, setComponentState, setPageState],
  );

  return useMemo(
    () => ({
      onInit,
      onProcess,
    }),
    [onInit, onProcess],
  );
};
